import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Button as AlertDialogAction } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { ArrowLeft, Plus } from "lucide-react";
import { ChlorineConfigHelpPanel } from "@/components/ChlorineConfigHelpPanel";
import { ChlorineConfigTable } from "@/components/ChlorineConfigTable";
import { ChlorineConfigModal } from "@/components/ChlorineConfigModal";

export default function ChlorineConfigManager() {
  const params = useParams();
  const [, navigate] = useLocation();
  const { user } = useAuth();

  const wellId = parseInt(params.wellId);
  if (isNaN(wellId)) {
    toast.error("Invalid well ID");
    navigate("/wells");
    return null;
  }

  const isEditable = user?.role === "supervisor" || user?.role === "admin";

  const { data: well } = trpc.wells.getById.useQuery({ id: wellId });
  const { data: configs } = trpc.chlorineConfig.listForWell.useQuery({ wellId });
  const utils = trpc.useUtils();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const deleteMutation = trpc.chlorineConfig.delete.useMutation({
    onSuccess: () => {
      toast.success("Config deleted successfully");
      utils.chlorineConfig.listForWell.invalidate({ wellId });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete config");
    },
  });

  const openModal = (config?: any) => {
    setEditingConfig(config || null);
    setModalOpen(true);
  };

  const handleSuccess = () => {
    utils.chlorineConfig.listForWell.invalidate({ wellId });
    setModalOpen(false);
  };

  const confirmDelete = () => {
    if (deleteId) {
      deleteMutation.mutate({ id: deleteId });
      setDeleteId(null);
    }
  };

  if (!well) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/wells")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Wells
            </Button>
            <h1 className="text-xl font-bold">
              Chlorine Config: {well.wellName}
            </h1>
          </div>
        </div>
      </header>

      <main className="container py-6 max-w-4xl">
        <ChlorineConfigHelpPanel />

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Configuration History</h2>
          {isEditable && (
            <Button onClick={() => openModal()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Config
            </Button>
          )}
        </div>

        <ChlorineConfigTable
          configs={configs || []}
          onEdit={openModal}
          onDelete={setDeleteId}
          isEditable={isEditable}
        />

        <ChlorineConfigModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          wellId={wellId}
          editingConfig={editingConfig}
          onSuccess={handleSuccess}
        />

        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Config</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure? This cannot be undone and will affect historical calculations if referenced.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}
